import { Badge, ACHIEVEMENTS } from '../constants/achievements';

export interface XPSource {
  action: string;
  amount: number;
  timestamp: Date;
}

export const XP_REWARDS = {
  VISIT_SECTION: 10,
  VISIT_COURT: 10,
  VISIT_RULES: 10,
  VISIT_SCORING: 10,
  VISIT_GAMES: 10,
  VISIT_AI_REF: 10,
  
  CLICK_COURT_ZONE: 5,
  EXPLORE_ALL_ZONES: 50,
  COMPLETE_QUIZ: 50,
  PERFECT_QUIZ_BONUS: 25,
  QUIZ_CORRECT_ANSWER: 10,
  PLAY_GAME: 30,
  COMPLETE_ALL_GAMES: 100,
  ASK_AI_QUESTION: 5,
  AI_MILESTONE_10: 25,
  AI_MILESTONE_25: 50,
  UNLOCK_BADGE: 0,
} as const;

export function calculateQuizXP(
  score: number,
  _totalQuestions: number,
  isPerfect: boolean
): number {
  const baseXP = XP_REWARDS.COMPLETE_QUIZ;
  const correctAnswerXP = score * XP_REWARDS.QUIZ_CORRECT_ANSWER;
  const perfectBonus = isPerfect ? XP_REWARDS.PERFECT_QUIZ_BONUS : 0;
  
  return baseXP + correctAnswerXP + perfectBonus;
}

export function calculateGameXP(
  _score: number,
  _previousBest?: number
): number {
  const baseXP = XP_REWARDS.PLAY_GAME;
  return baseXP;
}

export interface ProgressMetrics {
  courtExploration: number;
  rulesCompleted: number;
  scoringPracticed: number;
  gamesPlayed: number;
  aiInteracted: number;
}

export function calculateOverallCompletion(metrics: ProgressMetrics): number {
  const weights = {
    courtExploration: 0.20,
    rulesCompleted: 0.20,
    scoringPracticed: 0.15,
    gamesPlayed: 0.25,
    aiInteracted: 0.20,
  };
  
  return Math.floor(
    metrics.courtExploration * weights.courtExploration +
    metrics.rulesCompleted * weights.rulesCompleted +
    metrics.scoringPracticed * weights.scoringPracticed +
    metrics.gamesPlayed * weights.gamesPlayed +
    metrics.aiInteracted * weights.aiInteracted
  );
}

export function checkBadgeUnlock(
  badge: Badge,
  progress: {
    sectionsVisited: Set<string>;
    courtZonesClicked: Set<string>;
    totalCourtZones: number;
    quizzesCompleted: string[];
    perfectQuizzes: string[];
    gamesPlayed: string[];
    aiQuestionsAsked: number;
    overallCompletion: number;
    totalBadges: number;
  }
): boolean {
  const { criteria } = badge;
  
  switch (criteria.type) {
    case 'visit':
      return progress.sectionsVisited.size >= (criteria.target || 0);
      
    case 'exploration':
      const explorationPercent = (progress.courtZonesClicked.size / progress.totalCourtZones) * 100;
      return explorationPercent >= (criteria.threshold || 0);
      
    case 'quiz':
      if (criteria.threshold === 100) {
        return progress.perfectQuizzes.length >= (criteria.target || 1);
      }
      return progress.quizzesCompleted.length >= (criteria.target || 1);
      
    case 'game':
      if (criteria.target && criteria.target > 1) {
        return progress.gamesPlayed.length >= criteria.target;
      }
      return progress.gamesPlayed.length >= 1;
      
    case 'ai':
      return progress.aiQuestionsAsked >= (criteria.target || 0);
      
    case 'completion':
      if (criteria.threshold) {
        return progress.overallCompletion >= criteria.threshold;
      }
      if (criteria.target) {
        return progress.totalBadges >= criteria.target;
      }
      return false;
      
    default:
      return false;
  }
}

export function checkForNewBadges(
  progress: Parameters<typeof checkBadgeUnlock>[1],
  currentBadges: string[]
): Badge[] {
  const newBadges: Badge[] = [];
  
  for (const badge of ACHIEVEMENTS) {
    if (currentBadges.includes(badge.id)) continue;
    if (checkBadgeUnlock(badge, progress)) {
      newBadges.push(badge);
    }
  }
  
  return newBadges;
}

export function formatXP(xp: number): string {
  return xp.toLocaleString('en-US');
}

export function getMotivationalMessage(completion: number): string {
  if (completion === 100) {
    return "\u{1F389} Perfect! You\'re a Padel Master!";
  } else if (completion >= 80) {
    return "\u2728 Almost there! Keep it up!";
  } else if (completion >= 60) {
    return "\u{1F525} You\'re on fire! Keep learning!";
  } else if (completion >= 40) {
    return "\u{1F44F} Great progress! You\'re getting there!";
  } else if (completion >= 20) {
    return "\u{1F44D} Nice start! Keep exploring!";
  } else {
    return "\u{1F3BE} Let\'s begin your Padel journey!";
  }
}

export function getNextMilestone(progress: Parameters<typeof checkBadgeUnlock>[1]): string {
  if (progress.sectionsVisited.size < 6) {
    return "Explore all sections";
  }
  
  if (progress.courtZonesClicked.size < progress.totalCourtZones) {
    return "Click all court zones";
  }
  
  if (progress.quizzesCompleted.length === 0) {
    return "Complete your first quiz";
  }
  
  if (progress.gamesPlayed.length === 0) {
    return "Play a mini-game";
  }

  if (progress.perfectQuizzes.length === 0) {
    return "Get 100% on a quiz";
  }
  
  return "Complete all achievements";
}
